import React, { useEffect, useState } from "react"
import { MDBDataTable } from "mdbreact"
import {
    Row,
    Col,
    Card,
    CardBody,
    CardTitle,
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Form,
    FormGroup,
    Label,
    Input,
    Badge,
} from "reactstrap"
import { connect } from "react-redux"
import { setBreadcrumbItems } from "../../store/actions"
import { meetingsAPI, projetsAPI, usersAPI } from "../../services/api"
import AlertModal from "../../components/Common/AlertModal"

const MeetingList = (props) => {
    document.title = "Réunions & Meetings | Ticket Management"

    const [meetings, setMeetings] = useState([])
    const [projets, setProjets] = useState([])
    const [users, setUsers] = useState([])
    const [modal, setModal] = useState(false)
    const [reportModal, setReportModal] = useState(false)
    const [selectedMeeting, setSelectedMeeting] = useState(null)
    const [isEdit, setIsEdit] = useState(false)

    const [formData, setFormData] = useState({
        titre: "",
        description: "",
        date_heure: "",
        duree_minutes: 60,
        lieu: "",
        lien_visio: "",
        projet_id: "",
        participant_ids: [],
    })

    const [alertConfig, setAlertConfig] = useState({
        isOpen: false,
        type: 'success',
        message: '',
        title: ''
    })

    const toggleAlert = () => setAlertConfig({ ...alertConfig, isOpen: !alertConfig.isOpen })

    const [compteRendu, setCompteRendu] = useState("")

    const breadcrumbItems = [
        { title: "Dashboard", link: "/dashboard" },
        { title: "Meetings", link: "#" },
    ]

    useEffect(() => {
        props.setBreadcrumbItems("Planning des Réunions", breadcrumbItems)
        fetchData()
    }, [])

    const fetchData = async () => {
        try {
            const [meetRes, projRes, userRes] = await Promise.all([
                meetingsAPI.getAll(),
                projetsAPI.getAll(),
                usersAPI.getAll()
            ])
            setMeetings(meetRes.data)
            setProjets(projRes.data)
            setUsers(userRes.data)
        } catch (error) {
            console.error("Erreur lors du chargement des données", error)
        }
    }

    const toggleModal = () => {
        setModal(!modal)
        if (!modal) {
            setFormData({
                titre: "",
                description: "",
                date_heure: "",
                duree_minutes: 60,
                lieu: "",
                lien_visio: "",
                projet_id: "",
                participant_ids: [],
            })
            setIsEdit(false)
            setSelectedMeeting(null)
        }
    }

    const handleEdit = (m) => {
        setSelectedMeeting(m)
        setFormData({
            titre: m.titre,
            description: m.description || "",
            date_heure: m.date_heure ? m.date_heure.substring(0, 16) : "",
            duree_minutes: m.duree_minutes || 60,
            lieu: m.lieu || "",
            lien_visio: m.lien_visio || "",
            projet_id: m.projet_id || "",
            participant_ids: m.participants?.map(p => p.id) || [],
        })
        setIsEdit(true)
        setModal(true)
    }

    const toggleReportModal = (meeting = null) => {
        setSelectedMeeting(meeting)
        setCompteRendu(meeting?.compte_rendu || "")
        setReportModal(!reportModal)
    }

    const handleDelete = async (id) => {
        if (window.confirm("Supprimer cette réunion ?")) {
            try {
                await meetingsAPI.delete(id)
                fetchData()
            } catch (error) {
                console.error("Erreur lors de la suppression", error)
            }
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        try {
            if (isEdit && selectedMeeting) {
                await meetingsAPI.update(selectedMeeting.id, formData)
            } else {
                await meetingsAPI.create(formData)
            }
            toggleModal()
            setAlertConfig({
                isOpen: true,
                type: 'success',
                title: isEdit ? 'Réunion modifiée' : 'Réunion planifiée',
                message: isEdit ? "Le meeting a été mis à jour avec succès." : "Le meeting a été planifié et les participants ont été notifiés."
            })
            fetchData()
        } catch (error) {
            console.error("Erreur lors de l'enregistrement", error)
            setAlertConfig({
                isOpen: true,
                type: 'error',
                title: 'Erreur',
                message: "Une erreur est survenue lors de l'enregistrement de la réunion."
            })
        }
    }

    const handleReportSubmit = async (e) => {
        e.preventDefault()
        try {
            await meetingsAPI.addCompteRendu(selectedMeeting.id, compteRendu)
            toggleReportModal()
            setAlertConfig({
                isOpen: true,
                type: 'success',
                title: 'Compte rendu enregistré',
                message: "Le compte rendu a été ajouté et la réunion est maintenant marquée comme terminée."
            })
            fetchData()
        } catch (error) {
            console.error("Erreur lors de l'enregistrement du compte-rendu", error)
            setAlertConfig({
                isOpen: true,
                type: 'error',
                title: 'Erreur',
                message: "Une erreur est survenue lors de l'enregistrement du compte-rendu."
            })
        }
    }

    const data = {
        columns: [
            { label: "Titre", field: "titre", sort: "asc" },
            { label: "Date & Heure", field: "date_heure", sort: "asc" },
            { label: "Lieu / Lien", field: "lieu", sort: "asc" },
            { label: "Projet", field: "projet", sort: "asc" },
            { label: "Participants", field: "participants", sort: "asc" },
            { label: "Statut", field: "statut", sort: "asc" },
            { label: "Actions", field: "actions", sort: "disabled" },
        ],
        rows: meetings.map((m) => ({
            titre: m.titre || "-",
            date_heure: m.date_heure ? new Date(m.date_heure).toLocaleString() : "-",
            lieu: m.lieu || (m.lien_visio ? <a href={m.lien_visio} target="_blank" rel="noreferrer" className="text-primary"><i className="mdi mdi-video me-1"></i>Visio</a> : "-"),
            projet: m.projet ? m.projet.nom : "-",
            participants: m.participants?.length || 0,
            statut: <Badge color={m.statut === 'termine' ? 'success' : 'primary'}>{m.statut.toUpperCase()}</Badge>,
            actions: (
                <div className="d-flex gap-2">
                    <Button color="info" size="sm" onClick={() => handleEdit(m)} title="Modifier">
                        <i className="mdi mdi-pencil"></i>
                    </Button>
                    <Button color="primary" size="sm" onClick={() => toggleReportModal(m)} title="Compte Rendu">
                        <i className="mdi mdi-file-document"></i>
                    </Button>
                    <Button color="danger" size="sm" onClick={() => handleDelete(m.id)} title="Supprimer">
                        <i className="mdi mdi-trash-can"></i>
                    </Button>
                </div>
            ),
        })),
    }

    return (
        <React.Fragment>
            <Row>
                <Col className="col-12">
                    <Card>
                        <CardBody>
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <CardTitle className="h4">Planning des Réunions</CardTitle>
                                <div className="d-flex gap-2">
                                    <Button color="primary" onClick={toggleModal}>
                                        <i className="mdi mdi-plus me-1"></i> Nouveau Meeting
                                    </Button>
                                </div>
                            </div>

                            <MDBDataTable
                                noBottomColumns
                                responsive
                                striped
                                bordered
                                data={data}
                                entriesLabel="Afficher"
                                searchLabel="Rechercher"
                                infoLabel={["Affichage de", "à", "sur", "entrées"]}
                                paginationLabel={["Précédent", "Suivant"]}
                                noRecordsFoundLabel="Aucun enregistrement trouvé"
                            />
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <Modal isOpen={modal} toggle={toggleModal} size="lg">
                <ModalHeader toggle={toggleModal}>
                    {isEdit ? "Modifier la réunion" : "Planifier une réunion"}
                </ModalHeader>
                <Form onSubmit={handleSubmit}>
                    <ModalBody>
                        <FormGroup>
                            <Label for="titre">Titre</Label>
                            <Input
                                type="text"
                                id="titre"
                                value={formData.titre}
                                onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
                                required
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label for="description">Description</Label>
                            <Input
                                type="textarea"
                                id="description"
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            />
                        </FormGroup>
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="date_heure">Date & Heure</Label>
                                    <Input
                                        type="datetime-local"
                                        id="date_heure"
                                        value={formData.date_heure}
                                        onChange={(e) => setFormData({ ...formData, date_heure: e.target.value })}
                                        required
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="duree_minutes">Durée (minutes)</Label>
                                    <Input
                                        type="number"
                                        id="duree_minutes"
                                        value={formData.duree_minutes}
                                        onChange={(e) => setFormData({ ...formData, duree_minutes: e.target.value })}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="lieu">Lieu / Salle</Label>
                                    <Input
                                        type="text"
                                        id="lieu"
                                        value={formData.lieu}
                                        onChange={(e) => setFormData({ ...formData, lieu: e.target.value })}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="lien_visio">Lien Visioconférence</Label>
                                    <Input
                                        type="text"
                                        id="lien_visio"
                                        value={formData.lien_visio}
                                        onChange={(e) => setFormData({ ...formData, lien_visio: e.target.value })}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        <FormGroup>
                            <Label for="projet_id">Projet Concerné</Label>
                            <Input
                                type="select"
                                id="projet_id"
                                value={formData.projet_id}
                                onChange={(e) => setFormData({ ...formData, projet_id: e.target.value })}
                            >
                                <option value="">Aucun projet spécifique</option>
                                {projets.map(p => <option key={p.id} value={p.id}>{p.nom}</option>)}
                            </Input>
                        </FormGroup>
                        <FormGroup>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <Label className="mb-0">Inviter des participants</Label>
                                <div className="form-check">
                                    <Input
                                        type="checkbox"
                                        id="selectAllParticipants"
                                        className="form-check-input"
                                        checked={formData.participant_ids.length === users.length && users.length > 0}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                // Sélectionner tous les participants
                                                setFormData({ ...formData, participant_ids: users.map(u => String(u.id)) });
                                            } else {
                                                // Désélectionner tous les participants
                                                setFormData({ ...formData, participant_ids: [] });
                                            }
                                        }}
                                    />
                                    <label className="form-check-label" htmlFor="selectAllParticipants">
                                        Sélectionner tout ({users.length})
                                    </label>
                                </div>
                            </div>
                            <Input
                                type="select"
                                multiple
                                value={formData.participant_ids}
                                onChange={(e) => {
                                    const values = Array.from(e.target.selectedOptions, option => option.value);
                                    setFormData({ ...formData, participant_ids: values });
                                }}
                                style={{ height: '150px' }}
                            >
                                {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
                            </Input>
                            <small className="text-muted">
                                Maintenez Ctrl pour sélectionner plusieurs participants •
                                <strong> {formData.participant_ids.length} sélectionné(s)</strong>
                            </small>
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={toggleModal}>Annuler</Button>
                        <Button color="primary" type="submit">
                            {isEdit ? "Mettre à jour" : "Notifier & Enregistrer"}
                        </Button>
                    </ModalFooter>
                </Form>
            </Modal>

            <Modal isOpen={reportModal} toggle={() => setReportModal(false)} size="lg">
                <ModalHeader toggle={() => setReportModal(false)}>Compte Rendu de Réunion</ModalHeader>
                <Form onSubmit={handleReportSubmit}>
                    <ModalBody>
                        <h5>{selectedMeeting?.titre}</h5>
                        <p className="text-muted">{selectedMeeting?.date_heure && new Date(selectedMeeting.date_heure).toLocaleString()}</p>
                        <FormGroup>
                            <Label for="cr">Compte Rendu (CR)</Label>
                            <Input
                                type="textarea"
                                id="cr"
                                rows="10"
                                placeholder="Détails de la réunion, décisions prises, actions à venir..."
                                value={compteRendu}
                                onChange={(e) => setCompteRendu(e.target.value)}
                                required
                            />
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => setReportModal(false)}>Annuler</Button>
                        <Button color="success" type="submit">Valider le CR & Terminer</Button>
                    </ModalFooter>
                </Form>
            </Modal>

            <AlertModal
                isOpen={alertConfig.isOpen}
                toggle={toggleAlert}
                type={alertConfig.type}
                title={alertConfig.title}
                message={alertConfig.message}
            />
        </React.Fragment>
    )
}

export default connect(null, { setBreadcrumbItems })(MeetingList)
